import React from 'react'
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import * as Yup from "yup";
import { toast } from 'sonner';
import { MaskedInput } from '@/components/form';
import referralService from '@/services/referral/referralService';
import { PublicHeader } from '@/layouts/main/header-public';
import { FormattedMessage } from 'react-intl';
import { useIntl } from 'react-intl';
// import { HeaderLogo } from '@/layouts/main/header';

const AttorneyRequest = () => {
    const { locale, formatMessage } = useIntl();
    const formikRef = useRef(null);
    const [Loading, setLoading] = useState(false)
    const usPhoneRegex = /^\d{10}$/;

    const [validationSchema, setValidationSchema] = useState(Yup.object().shape({}));

    useEffect(() => {
        // Recreate the validation schema whenever the language (locale) changes
        const schema = Yup.object().shape({
            first_name: Yup.string().required(formatMessage({ id: 'validation.first_name_required' })),
            last_name: Yup.string().required(formatMessage({ id: 'validation.last_name_required' })),
            email: Yup.string()
                .email(formatMessage({ id: 'validation.invalid_email' }))
                .required(formatMessage({ id: 'validation.email_required' })),
            number: Yup.string()
                .transform(value => (value ? value.replace(/\D/g, '') : value))
                .matches(usPhoneRegex, formatMessage({ id: 'validation.invalid_phone_number' }))
                .required(formatMessage({ id: 'validation.number_required' })),
            address: Yup.string().required(formatMessage({ id: 'validation.address_required' })),
            city: Yup.string().required(formatMessage({ id: 'validation.city_required' })),
            state: Yup.string().required(formatMessage({ id: 'validation.state_required' })),
            zip: Yup.string()
                .matches(/^\d{5}(-\d{4})?$/, formatMessage({ id: 'validation.invalid_zip_code' }))
                .required(formatMessage({ id: 'validation.zip_required' })),
            comments: Yup.string().nullable().max(1000, formatMessage({ id: 'validation.comments_max_length' }))
        });

        // Set the new schema
        setValidationSchema(schema);
        console.log('===?', locale, formatMessage);


    }, [locale, formatMessage]);


    const initialValues = {
        first_name: '',
        last_name: '',
        email: '',
        number: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        comments: '',
    }

    const handleSubmit = async (values, resetForm, setErrors) => {
        setLoading(true)
        try {
            const { status, data } = await referralService.createAttorneyRequest(values);
            if (status) {
                toast.success(formatMessage({ id: 'validation.request_submitted_successfully' }), {
                    description: formatMessage({ id: 'validation.request_submitted_message' })
                });
                resetForm();
            }
        } catch (error) {
            console.error('Error', error);
            if (error.message === '422 Unprocessable Entity. Validation failed.') {
                const formattedErrors = Object.fromEntries(
                    Object.entries(error.errors || {}).map(([key, value]) => [key, value[0]])
                );
                setErrors(formattedErrors);
                toast.warning(formatMessage({ id: 'validation.validation_error' }), {
                    description: formatMessage({ id: 'validation.validation_message' })
                });
                return
            }
            toast.error(formatMessage({ id: 'validation.failed_to_submit_request' }), {
                description: formatMessage({ id: 'validation.error_message' })
            });
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className='w-full h-full'>
            <PublicHeader />
            <Formik
                innerRef={formikRef}
                enableReinitialize={true}
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnBlur={true}
                validateOnChange={true}
                validateOnMount={true}
                onSubmit={(values, { resetForm, setErrors }) => {
                    handleSubmit(values, resetForm, setErrors);
                }}
            >
                {({ formik, errors, values, handleChange, handleSubmit, setFieldValue, setValues, setTouched, setErrors }) => {
                    return (
                        <Form key={locale}>
                            <div className='card pb-2.5 container-fixed mx-auto mt-6'>
                                <div className="card-header">
                                    <h3 className="card-title">
                                        <FormattedMessage id="REFERRAL.ATTORNEY_REQUEST" />
                                    </h3>
                                </div>

                                <div className='card-body max-sm:p-0'>
                                    <div className='flex items-center mb-6'>
                                        <span className='w-12 h-0.5 bg-border inline-block mr-2'></span>
                                        <p className='text-sm'><FormattedMessage id="REFERRAL.ATTORNEY_INFORMATION" /></p>
                                    </div>

                                    <div className="grid gap-3 sm:grid-cols-4 xs:grid-cols-2">
                                        <div>
                                            <label className="mb-1 form-label"><FormattedMessage id="REFERRAL.FIRST_NAME" /> <span className='pl-px text-red-600'>*</span></label>
                                            <Field className="input" type="text" name="first_name" />
                                            <ErrorMessage name="first_name" component="div" className="text-red-500 text-[11px]" />
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label"><FormattedMessage id="REFERRAL.LAST_NAME" /> <span className='pl-px text-red-600'>*</span></label>
                                            <Field className="input" type="text" name="last_name" />
                                            <ErrorMessage name="last_name" component="div" className="text-red-500 text-[11px]" />
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label"><FormattedMessage id="REFERRAL.EMAIL" /><span className='pl-px text-red-600'>*</span></label>
                                            <Field className="input" type="text" name="email" />
                                            <ErrorMessage name="email" component="div" className="text-red-500 text-[11px]" />
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label"><FormattedMessage id="REFERRAL.PHONE_NUMBER" /><span className='pl-px text-red-600'>*</span></label>
                                            <MaskedInput className="input no-spinner" name="number" />
                                            {/* <ErrorMessage name="number" component="div" className="text-red-500 text-[11px]" /> */}
                                        </div>
                                    </div>

                                    <div className='flex items-center mt-6'>
                                        <span className='w-12 h-0.5 bg-border inline-block mr-2'></span>
                                        <p className='text-sm'><FormattedMessage id="REFERRAL.ADDRESS_INFORMATION" /></p>
                                    </div>

                                    <div className="grid gap-3 sm:grid-cols-4 xs:grid-cols-2 my-5">
                                        <div>
                                            <label className="mb-1 form-label"><FormattedMessage id="REFERRAL.ADDRESS" /> <span className='pl-px text-red-600'>*</span></label>
                                            <Field className="input" type="text" name="address" />
                                            <ErrorMessage name="address" component="div" className="text-red-500 text-[11px]" />
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label"><FormattedMessage id="REFERRAL.CITY" /> <span className='pl-px text-red-600'>*</span></label>
                                            <Field className="input" type="text" name="city" />
                                            <ErrorMessage name="city" component="div" className="text-red-500 text-[11px]" />
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label"><FormattedMessage id="REFERRAL.STATE" /> <span className='pl-px text-red-600'>*</span></label>
                                            <Field className="input" type="text" name="state" />
                                            <ErrorMessage name="state" component="div" className="text-red-500 text-[11px]" />
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label"><FormattedMessage id="REFERRAL.ZIP" /> <span className='pl-px text-red-600'>*</span></label>
                                            <Field className="input" type="text" name="zip" />
                                            <ErrorMessage name="zip" component="div" className="text-red-500 text-[11px]" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="mb-1 form-label gap-1"><FormattedMessage id="REFERRAL.COMMENTS" /> <span className="text-[11px]"><FormattedMessage id="REFERRAL.OPTIONAL" /></span></label>
                                        <Field
                                            as="textarea"
                                            name='comments'
                                            rows={5}
                                            className="input pt-2 h-[150px]"
                                        />
                                        <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
                                            <span>
                                                {values.comments.length}/{1000} <FormattedMessage id="REFERRAL.CHARACTERS" />
                                            </span>
                                            <ErrorMessage
                                                name='comments'
                                                component="div"
                                                className="text-[11px] text-red-500"
                                            />
                                        </div>
                                    </div>

                                    <div className="w-full inline-flex items-center justify-end gap-2 mt-2.5">
                                        <button type="submit" disabled={Loading} className="btn btn-primary btn-sm">
                                            {Loading ? <FormattedMessage id="USER.MENU.PLEASE_WAIT" /> : <FormattedMessage id="REFERRAL.SUBMIT_REQUEST" />}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </Form>
                    )
                }}
            </Formik>
        </div>
    )
}

export { AttorneyRequest }